import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import { TEACHER_COUNT_LABEL_EN } from '../../constants/siteStats';
import './TeamPage.css';

const TeamPage = () => {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: 'Yeongu Kang',
      role: 'CEO & Founder',
      avatar: 'YK',
      details: [
        'B.S. in Computer Engineering, Kwangwoon University',
        'Founded Plactical Co., Ltd. in 2023',
        'Led the development of WhatQuiz AI, achieving 10,000+ MAU',
        'Winner of Preliminary Startup Package (Ministry of SMEs and Startups)',
      ],
      linkedin: 'https://www.linkedin.com/in/%EB%9E%99%ED%8B%B0%EC%BB%AC-%ED%94%8C-080832320/',
    },
    {
      name: 'Jun Seo',
      role: 'CTO & Co-Founder',
      avatar: 'JS',
      details: [
        'B.S. in Computer Engineering, Kwangwoon University',
        'Head of Architecture Design & Core Engine Development',
        'Lead Developer of HyperGraph RAG Engine',
        '6+ years of software development experience',
      ],
      linkedin: 'https://www.linkedin.com/in/%EB%9E%99%ED%8B%B0%EC%BB%AC-%ED%94%8C-080832320/',
    },
    {
      name: 'Sunghyun Park',
      role: 'Head of AI Lab',
      avatar: 'SP',
      details: [
        'B.S. in Computer Engineering, Kwangwoon University',
        'Director of Company-Affiliated Research Institute (KOITA)',
        'Specialist in AI Model Fine-tuning (RAG/SLM)',
        'Leads Data Pipeline & ML Infrastructure',
      ],
      linkedin: 'https://www.linkedin.com/in/%EB%9E%99%ED%8B%B0%EC%BB%AC-%ED%94%8C-080832320/',
    },
    {
      name: 'Serim Choi',
      role: 'Product Manager & Education Lead',
      avatar: 'SC',
      details: [
        'B.A., Seoul Women\'s University',
        'Certified Teacher (Level 2) & Education Specialist',
        'Leads User Experience (UX) & Service Planning',
        `Manages feedback integration for ${TEACHER_COUNT_LABEL_EN} teachers`,
      ],
      linkedin: 'https://www.linkedin.com/in/%EB%9E%99%ED%8B%B0%EC%BB%AC-%ED%94%8C-080832320/',
    },
  ];

  const certifications = [
    {
      icon: 'building',
      title: 'Venture Business Certification',
      desc: 'Certified by the Ministry of SMEs and Startups, Republic of Korea',
    },
    {
      icon: 'flask',
      title: 'R&D Center Certification',
      desc: 'Officially recognized Company-Affiliated Research Institute (KOITA)',
    },
    {
      icon: 'handshake',
      title: 'Government-Backed',
      desc: 'Backed by KIBO (Korea Technology Finance Corp.) & KOSME (Korea SMEs and Startups Agency)',
    },
  ];

  return (
    <div className="team-page">
      <Helmet>
        <title>About Us - WhatQuiz AI | Plactical Co., Ltd.</title>
        <meta name="description" content="Meet the team behind WhatQuiz AI. Plactical Co., Ltd. is an innovative EdTech startup empowering teachers with AI technology." />
      </Helmet>

      {/* Hero */}
      <section className="team-hero">
        <div className="team-hero-inner">
          <span className="team-hero-label">About Us</span>
          <h1 className="team-hero-title">
            Empowering Teachers with{' '}
            <span className="team-hero-highlight">AI Technology</span>
          </h1>
          <p className="team-hero-desc">
            WhatQuiz AI is an innovative EdTech service developed and operated by
            Plactical Co., Ltd. We automate administrative tasks for teachers
            using our proprietary HyperGraph RAG technology, powered by Google
            Cloud and Gemini.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="team-mission">
        <div className="team-mission-inner">
          <h2 className="team-mission-title">Our Mission</h2>
          <p className="team-mission-text">
            We believe teachers should spend their time inspiring students — not
            buried in paperwork. WhatQuiz AI automates student record writing,
            performance assessment grading, and document inspection, giving
            teachers back hours every week. Built entirely on the Google Cloud
            ecosystem, we leverage Gemini to deliver the highest quality AI
            experience in education.
          </p>
        </div>
      </section>

      {/* Team Members */}
      <section className="team-members">
        <div className="team-members-inner">
          <h2 className="team-section-title">Leadership & Engineering Team</h2>
          <p className="team-section-subtitle">
            Computer engineering specialists from Kwangwoon University,
            dedicated to transforming education with AI.
          </p>

          <div className="team-grid">
            {teamMembers.map((member) => (
              <div key={member.name} className="team-card">
                <div className="team-card-header">
                  <div className="team-card-avatar">{member.avatar}</div>
                  <div className="team-card-info">
                    <h3 className="team-card-name">{member.name}</h3>
                    <p className="team-card-role">{member.role}</p>
                  </div>
                </div>

                <div className="team-card-details">
                  {member.details.map((detail, idx) => (
                    <div key={idx} className="team-card-detail">
                      <span className="team-card-detail-icon">•</span>
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>

                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="team-card-linkedin"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn Profile
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="team-certifications">
        <div className="team-certifications-inner">
          <h2 className="team-section-title">Company Certifications & Backing</h2>
          <p className="team-section-subtitle">
            Recognized and supported by the Korean government
          </p>

          <div className="team-cert-grid">
            {certifications.map((cert) => (
              <div key={cert.title} className="team-cert-card">
                <div className="team-cert-icon"><NamedGrayIcon name={cert.icon} /></div>
                <h3 className="team-cert-title">{cert.title}</h3>
                <p className="team-cert-desc">{cert.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="team-cta">
        <div className="team-cta-inner">
          <h2 className="team-cta-title">Try WhatQuiz AI Today</h2>
          <p className="team-cta-desc">
            Join {TEACHER_COUNT_LABEL_EN} teachers who are already saving hours every week
            with AI-powered automation.
          </p>
          <button
            className="team-cta-btn"
            onClick={() => navigate('/home')}
          >
            Get Started Free
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
